import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Capa,
  CapaStatus,
  CapaVerificationResult,
  CapaEffectivenessResult,
} from '../types';
import { capaService } from '../capaService';

interface CapaDetailModalProps {
  capa: Capa;
  onClose: () => void;
  onRefresh: () => void;
}

export const CapaDetailModal: React.FC<CapaDetailModalProps> = ({
  capa: initialCapa,
  onClose,
  onRefresh,
}) => {
  const [capa, setCapa] = useState<Capa>(initialCapa);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'CORRECTIONS_RCA' | 'LINKS' | 'VERIFICATION_EFF' | 'EXTENSIONS_CLOSURE'>('OVERVIEW');
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Correction Form
  const [showAddCorrection, setShowAddCorrection] = useState(false);
  const [corrDesc, setCorrDesc] = useState('');
  const [corrContainment, setCorrContainment] = useState('');
  const [corrTakenBy, setCorrTakenBy] = useState(capa.ownerName);

  // Root Cause Form
  const [showAddRootCause, setShowAddRootCause] = useState(false);
  const [rcMethod, setRcMethod] = useState<'FIVE_WHY' | 'FISHBONE' | 'BARRIER_ANALYSIS' | 'HFACS'>('FIVE_WHY');
  const [rcType, setRcType] = useState('PROCESS');
  const [rcDesc, setRcDesc] = useState('');

  // Finding Link Form
  const [showAddFindingLink, setShowAddFindingLink] = useState(false);
  const [linkFindingId, setLinkFindingId] = useState('');
  const [linkFindingNo, setLinkFindingNo] = useState('');
  const [linkFindingTitle, setLinkFindingTitle] = useState('');

  // Action Link Form
  const [showAddActionLink, setShowAddActionLink] = useState(false);
  const [linkActionId, setLinkActionId] = useState('');
  const [linkActionType, setLinkActionType] = useState<'CORRECTIVE' | 'PREVENTIVE'>('CORRECTIVE');
  const [linkActionTitle, setLinkActionTitle] = useState('');
  const [linkActionOwner, setLinkActionOwner] = useState(capa.ownerId);

  // Verification Form (SoD)
  const [showAddVerification, setShowAddVerification] = useState(false);
  const [verifId, setVerifId] = useState('VERIF-AUDITOR-01');
  const [verifName, setVerifName] = useState('Independent Lead Auditor');
  const [verifResult, setVerifResult] = useState<CapaVerificationResult>('EFFECTIVE');
  const [verifNotes, setVerifNotes] = useState('');

  // Effectiveness Form
  const [showAddEffectiveness, setShowAddEffectiveness] = useState(false);
  const [effMetric, setEffMetric] = useState('Recurrence Rate (Discrepancy Rate)');
  const [effCriteria, setEffCriteria] = useState('Zero occurrences across past 90 days');
  const [effBaseline, setEffBaseline] = useState('4 events / quarter');
  const [effTarget, setEffTarget] = useState('0 events / quarter');
  const [effActual, setEffActual] = useState('0 events');
  const [effDate, setEffDate] = useState(new Date().toISOString().substring(0, 10));
  const [effResult, setEffResult] = useState<CapaEffectivenessResult>('EFFECTIVE');
  const [effNotes, setEffNotes] = useState('');

  // Extension Form
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extNewDate, setExtNewDate] = useState(
    new Date(Date.now() + 30 * 86400000).toISOString().substring(0, 10)
  );
  const [extReason, setExtReason] = useState('');
  const [extRisk, setExtRisk] = useState('');

  // Closure Form
  const [closeComment, setCloseComment] = useState('All corrective and preventive actions verified, effectiveness criteria satisfied.');
  // Reopen Form
  const [reopenReason, setReopenReason] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);

  // Plan Approval Notes
  const [planApprovalNotes, setPlanApprovalNotes] = useState('');

  useEffect(() => {
    reloadCapa();
  }, [initialCapa.id]);

  const reloadCapa = async () => {
    try {
      setLoading(true);
      const data = await capaService.getCapaById(initialCapa.id);
      setCapa(data);
    } catch (err: any) {
      setActionError(err.message || 'Failed to load CAPA details.');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanApproval = async () => {
    try {
      setActionError(null);
      await capaService.approvePlan(capa.id, 'DIR-QUALITY', 'Quality Director', planApprovalNotes);
      setActionSuccess('CAPA Management Plan approved and transitioned to implementation.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddCorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.addCorrection(capa.id, {
        description: corrDesc,
        containmentAction: corrContainment,
        takenBy: corrTakenBy,
      });
      setCorrDesc('');
      setCorrContainment('');
      setShowAddCorrection(false);
      setActionSuccess('Immediate containment action recorded successfully.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddRootCause = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.addRootCause(capa.id, {
        method: rcMethod,
        rootCauseType: rcType,
        description: rcDesc,
        verifiedBy: 'SAFETY-SPECIALIST',
      });
      setRcDesc('');
      setShowAddRootCause(false);
      setActionSuccess('Root cause analysis statement logged successfully.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleLinkFinding = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.linkFinding(capa.id, {
        findingId: linkFindingId || 'FND-AUTO-' + Date.now(),
        findingNumber: linkFindingNo,
        title: linkFindingTitle,
      });
      setLinkFindingNo('');
      setLinkFindingTitle('');
      setShowAddFindingLink(false);
      setActionSuccess('Audit finding linked to CAPA record.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleLinkAction = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.linkAction(capa.id, {
        actionId: linkActionId || 'ACT-CENTRAL-' + Date.now(),
        actionType: linkActionType,
        title: linkActionTitle,
        ownerId: linkActionOwner,
      });
      setLinkActionTitle('');
      setLinkActionId('');
      setShowAddActionLink(false);
      setActionSuccess('Central action item linked to CAPA record.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.addVerification(capa.id, {
        verifierId: verifId,
        verifierName: verifName,
        result: verifResult,
        notes: verifNotes,
      });
      setVerifNotes('');
      setShowAddVerification(false);
      setActionSuccess('Independent verification recorded successfully.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleAddEffectiveness = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.addEffectivenessCheck(capa.id, {
        metricName: effMetric,
        criteria: effCriteria,
        baselineValue: effBaseline,
        targetValue: effTarget,
        actualValue: effActual,
        evaluationDate: effDate,
        result: effResult,
        notes: effNotes,
      });
      setEffNotes('');
      setShowAddEffectiveness(false);
      setActionSuccess('Long-term effectiveness test recorded successfully.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleRequestExtension = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.requestExtension(capa.id, {
        newDueDate: extNewDate,
        reason: extReason,
        riskAssessment: extRisk,
        requesterId: capa.ownerId,
        requesterName: capa.ownerName,
      });
      setExtReason('');
      setExtRisk('');
      setShowExtensionModal(false);
      setActionSuccess('Due date extension request submitted.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleApproveExtension = async (extensionId: string) => {
    try {
      setActionError(null);
      await capaService.approveExtension(extensionId, 'DIR-QUALITY', 'Quality Director');
      setActionSuccess('Extension approved and due date updated.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleCloseCapa = async () => {
    try {
      setActionError(null);
      await capaService.closeCapa(capa.id, 'DIR-QUALITY', closeComment);
      setActionSuccess('CAPA closed and formally sealed.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const handleReopenCapa = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setActionError(null);
      await capaService.reopenCapa(capa.id, 'LEAD-AUDITOR', reopenReason);
      setReopenReason('');
      setShowReopenModal(false);
      setActionSuccess('CAPA record reopened for investigation.');
      await reloadCapa();
      onRefresh();
    } catch (err: any) {
      setActionError(err.message);
    }
  };

  const isClosed = capa.status === 'CLOSED';
  const isOverdue = !isClosed && capa.status !== 'CANCELLED' && new Date(capa.dueDate) < new Date();

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                {capa.capaNumber}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '4px' }}>
                {capa.status}
              </span>
              {capa.isSystemic && (
                <span style={{ fontSize: '11px', fontWeight: 700, background: '#f3e8ff', color: '#6b21a8', padding: '2px 8px', borderRadius: '4px', border: '1px solid #e9d5ff' }}>
                  SYSTEMIC CAPA
                </span>
              )}
              {isOverdue && (
                <span style={{ fontSize: '11px', fontWeight: 700, background: '#e11d48', color: '#ffffff', padding: '2px 8px', borderRadius: '4px' }}>
                  OVERDUE
                </span>
              )}
            </div>
            <h3 style={{ fontSize: '19px', fontWeight: 700, color: '#0f172a', margin: 0 }}>{capa.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Notifications / Alerts */}
        {actionError && (
          <div className="mx-6 mt-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
            <strong>Error:</strong> {actionError}
          </div>
        )}
        {actionSuccess && (
          <div className="mx-6 mt-3 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs flex justify-between items-center">
            <span><strong>Success:</strong> {actionSuccess}</span>
            <button onClick={() => setActionSuccess(null)} className="text-emerald-700 font-bold ml-2">✕</button>
          </div>
        )}

        {/* Tabs Bar */}
        <div className="px-6 bg-gray-50 border-b border-gray-200 flex space-x-6 text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab('OVERVIEW')}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'OVERVIEW'
                ? 'border-blue-600 text-blue-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview & Plan
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('CORRECTIONS_RCA')}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'CORRECTIONS_RCA'
                ? 'border-blue-600 text-blue-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Containment & RCA ({capa.corrections?.length || 0} / {capa.rootCauses?.length || 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('LINKS')}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'LINKS'
                ? 'border-blue-600 text-blue-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Linked Findings & Actions ({capa.findingLinks?.length || 0} / {capa.actionLinks?.length || 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('VERIFICATION_EFF')}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'VERIFICATION_EFF'
                ? 'border-blue-600 text-blue-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Verification & Effectiveness ({capa.verifications?.length || 0} / {capa.effectivenessChecks?.length || 0})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('EXTENSIONS_CLOSURE')}
            className={`py-3 border-b-2 transition-colors ${
              activeTab === 'EXTENSIONS_CLOSURE'
                ? 'border-blue-600 text-blue-700 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Extensions & Closure ({capa.extensions?.length || 0})
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="p-6 overflow-y-auto flex-1 text-xs text-gray-700 space-y-6">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'OVERVIEW' && (
            <div className="space-y-6">
              {/* Problem statement callout */}
              <div className="bg-amber-50/50 border border-amber-200 rounded-xl p-4">
                <span className="text-xs font-bold text-amber-800 uppercase tracking-wider block mb-1">
                  Problem Statement
                </span>
                <p className="text-gray-900 leading-relaxed font-medium text-sm">
                  {capa.problemStatement}
                </p>
                {capa.description && (
                  <p className="text-gray-600 text-xs mt-2 italic">
                    {capa.description}
                  </p>
                )}
              </div>

              {/* Grid Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs">
                <div>
                  <span className="text-gray-500 block font-medium">CAPA Type</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.capaType}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Category</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.category}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Priority / Severity</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.priority} / {capa.severity}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Trigger Source</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.triggerSource} {capa.triggerReference ? `(${capa.triggerReference})` : ''}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Department</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.department}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Owner</span>
                  <span className="font-semibold text-gray-900 text-sm">{capa.ownerName}</span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Due Date</span>
                  <span className={`font-semibold text-sm font-mono ${isOverdue ? 'text-rose-600 font-bold' : 'text-gray-900'}`}>
                    {capa.dueDate ? capa.dueDate.substring(0, 10) : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block font-medium">Cost (Est. / Actual)</span>
                  <span className="font-semibold text-gray-900 text-sm">${capa.estimatedCost || 0} / ${capa.actualCost || 0}</span>
                </div>
              </div>

              {/* Management Plan Approval Card */}
              {capa.requiresApproval && (
                <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-4">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                    <div>
                      <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">
                        CAPA Management & Implementation Plan Approval
                      </h4>
                      <p className="text-xs text-blue-700 mt-0.5">
                        {capa.approverName
                          ? `Approved By: ${capa.approverName}`
                          : 'Pending formal review and approval by Quality Director.'}
                      </p>
                    </div>
                    {!isClosed && capa.status !== 'APPROVED' && capa.status !== 'IMPLEMENTATION' && capa.status !== 'VERIFICATION' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          placeholder="Approval notes..."
                          value={planApprovalNotes}
                          onChange={(e) => setPlanApprovalNotes(e.target.value)}
                          className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 bg-white"
                        />
                        <button
                          type="button"
                          onClick={handlePlanApproval}
                          className="btn-primary"
                        >
                          Approve & Launch Plan
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status History Timeline */}
              <div>
                <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                  Status History & Audit Trail
                </h4>
                <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
                  {capa.statusHistory && capa.statusHistory.length > 0 ? (
                    capa.statusHistory.map((h) => (
                      <div key={h.id} className="text-xs flex items-center justify-between border-b border-gray-100 pb-2 last:border-none last:pb-0">
                        <div>
                          <span className="font-semibold text-blue-700">{h.toStatus}</span>
                          {h.reason && <span className="text-gray-500 ml-2 italic">({h.reason})</span>}
                        </div>
                        <div className="text-gray-400 font-mono text-[11px]">
                          {h.changedBy} • {h.createdAt.substring(0, 19).replace('T', ' ')}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No historical status records logged.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CORRECTIONS & ROOT CAUSE */}
          {activeTab === 'CORRECTIONS_RCA' && (
            <div className="space-y-6">
              {/* Corrections Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Immediate Containment Actions</h3>
                    <p className="text-xs text-gray-500">Immediate containment to isolate operational risk prior to RCA completion.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddCorrection(!showAddCorrection)}
                      className="btn-secondary"
                    >
                      {showAddCorrection ? 'Cancel' : '+ Add Containment'}
                    </button>
                  )}
                </div>

                {showAddCorrection && (
                  <form onSubmit={handleAddCorrection} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-field">
                      <label>Containment Measure Description *</label>
                      <input
                        type="text"
                        required
                        value={corrDesc}
                        onChange={(e) => setCorrDesc(e.target.value)}
                        placeholder="e.g. Flight dispatch planning rule placed on mandatory manual verification."
                      />
                    </div>
                    <div className="form-field">
                      <label>Quarantine / Operational Isolation Action</label>
                      <input
                        type="text"
                        value={corrContainment}
                        onChange={(e) => setCorrContainment(e.target.value)}
                        placeholder="e.g. 12 routes flagged for supervisory dispatch signoff."
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Save Containment Action
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.corrections && capa.corrections.length > 0 ? (
                    capa.corrections.map((c) => (
                      <div key={c.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs">
                        <div className="font-semibold text-gray-900">{c.description}</div>
                        {c.containmentAction && (
                          <div className="text-gray-600 mt-1"><span className="font-medium text-amber-800">Isolation:</span> {c.containmentAction}</div>
                        )}
                        <div className="text-[11px] text-gray-400 mt-1 font-mono">
                          Performed By: {c.takenBy} • {c.takenAt.substring(0, 10)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No immediate containment records logged.</div>
                  )}
                </div>
              </div>

              {/* Root Cause Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Root Cause Analysis (RCA)</h3>
                    <p className="text-xs text-gray-500">Underlying systematic factor identification using 5-Whys, Ishikawa, or HFACS.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddRootCause(!showAddRootCause)}
                      className="btn-secondary"
                    >
                      {showAddRootCause ? 'Cancel' : '+ Add Root Cause'}
                    </button>
                  )}
                </div>

                {showAddRootCause && (
                  <form onSubmit={handleAddRootCause} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Methodology</label>
                        <select
                          value={rcMethod}
                          onChange={(e) => setRcMethod(e.target.value as any)}
                        >
                          <option value="FIVE_WHY">5-Whys Analysis</option>
                          <option value="FISHBONE">Ishikawa (Fishbone)</option>
                          <option value="BARRIER_ANALYSIS">Barrier Analysis</option>
                          <option value="HFACS">HFACS (Human Factors)</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Root Cause Classification</label>
                        <input
                          type="text"
                          value={rcType}
                          onChange={(e) => setRcType(e.target.value)}
                          placeholder="e.g. SYSTEMIC_PROCESS_GAP"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Root Cause Statement & Evidence *</label>
                      <textarea
                        required
                        rows={3}
                        value={rcDesc}
                        onChange={(e) => setRcDesc(e.target.value)}
                        placeholder="e.g. 5-Why: Why #5: No secondary validation gate established for dispatch software configuration update."
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Save Root Cause Statement
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.rootCauses && capa.rootCauses.length > 0 ? (
                    capa.rootCauses.map((rc) => (
                      <div key={rc.id} className="p-3 bg-purple-50/30 border border-purple-200 rounded-xl text-xs">
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-purple-800 bg-purple-100 px-2 py-0.5 rounded text-[10px]">
                            {rc.method}
                          </span>
                          <span className="font-medium text-gray-600 text-[11px]">{rc.rootCauseType}</span>
                        </div>
                        <p className="mt-1 text-gray-800 font-medium">{rc.description}</p>
                        <div className="text-[11px] text-gray-400 mt-1 font-mono">
                          Verified By: {rc.verifiedBy || 'Quality Assurance'} • {rc.completedAt.substring(0, 10)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No root cause analysis recorded yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: LINKS (FINDINGS & CENTRAL ACTIONS) */}
          {activeTab === 'LINKS' && (
            <div className="space-y-6">
              {/* Architecture Boundary Note */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-700">
                <span className="font-bold text-slate-900 block mb-0.5">Architectural Rule: CAPA ≠ Finding ≠ Action ≠ Task</span>
                CAPA owns the high-level remediation case. Audit non-conformances (Findings) are linked as triggers; execution tasks are bridged to the Central Actions register.
              </div>

              {/* Finding Links */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Linked Audit Findings</h3>
                    <p className="text-xs text-gray-500">Discrepancies and audit findings that triggered or are addressed by this CAPA.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddFindingLink(!showAddFindingLink)}
                      className="btn-secondary"
                    >
                      {showAddFindingLink ? 'Cancel' : '+ Link Finding'}
                    </button>
                  )}
                </div>

                {showAddFindingLink && (
                  <form onSubmit={handleLinkFinding} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Finding Number *</label>
                        <input
                          type="text"
                          required
                          value={linkFindingNo}
                          onChange={(e) => setLinkFindingNo(e.target.value)}
                          placeholder="e.g. FND-2026-000001"
                        />
                      </div>
                      <div className="form-field">
                        <label>Finding ID (Optional)</label>
                        <input
                          type="text"
                          value={linkFindingId}
                          onChange={(e) => setLinkFindingId(e.target.value)}
                          placeholder="FND-01"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Finding Title / Summary *</label>
                      <input
                        type="text"
                        required
                        value={linkFindingTitle}
                        onChange={(e) => setLinkFindingTitle(e.target.value)}
                        placeholder="e.g. Contingency fuel reduction unauthorized departure"
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Link Finding
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.findingLinks && capa.findingLinks.length > 0 ? (
                    capa.findingLinks.map((fl) => (
                      <div key={fl.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs flex justify-between items-center">
                        <div>
                          <span className="font-mono font-bold text-blue-700 mr-2">{fl.findingNumber}</span>
                          <span className="font-medium text-gray-800">{fl.title}</span>
                        </div>
                        <div className="text-gray-400 font-mono text-[11px]">
                          {fl.linkedAt.substring(0, 10)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No linked audit findings.</div>
                  )}
                </div>
              </div>

              {/* Action Links */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Linked Central Actions</h3>
                    <p className="text-xs text-gray-500">Specific corrective and preventive work packages managed in the Actions module.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddActionLink(!showAddActionLink)}
                      className="btn-secondary"
                    >
                      {showAddActionLink ? 'Cancel' : '+ Link Action'}
                    </button>
                  )}
                </div>

                {showAddActionLink && (
                  <form onSubmit={handleLinkAction} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Action Type</label>
                        <select
                          value={linkActionType}
                          onChange={(e) => setLinkActionType(e.target.value as any)}
                        >
                          <option value="CORRECTIVE">Corrective Action</option>
                          <option value="PREVENTIVE">Preventive Action</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Action ID / Code *</label>
                        <input
                          type="text"
                          required
                          value={linkActionId}
                          onChange={(e) => setLinkActionId(e.target.value)}
                          placeholder="ACT-2026-0001"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Action Title *</label>
                      <input
                        type="text"
                        required
                        value={linkActionTitle}
                        onChange={(e) => setLinkActionTitle(e.target.value)}
                        placeholder="e.g. OFP Software dual verification algorithm rollout"
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Link Action Item
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.actionLinks && capa.actionLinks.length > 0 ? (
                    capa.actionLinks.map((al) => (
                      <div key={al.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs flex justify-between items-center">
                        <div>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mr-2 ${
                            al.actionType === 'CORRECTIVE' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {al.actionType}
                          </span>
                          <span className="font-mono font-bold text-gray-800 mr-2">{al.actionId}</span>
                          <span className="font-medium text-gray-800">{al.title}</span>
                        </div>
                        <div className="text-gray-500 font-mono text-[11px]">
                          {al.status}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No linked central action items.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: VERIFICATION & EFFECTIVENESS */}
          {activeTab === 'VERIFICATION_EFF' && (
            <div className="space-y-6">
              {/* Verification Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Independent Verification</h3>
                    <p className="text-xs text-gray-500">
                      Segregation of Duties (SoD): Verifier must be independent from the CAPA owner and action assignees.
                    </p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddVerification(!showAddVerification)}
                      className="btn-secondary"
                    >
                      {showAddVerification ? 'Cancel' : '+ Add Verification'}
                    </button>
                  )}
                </div>

                {showAddVerification && (
                  <form onSubmit={handleAddVerification} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Verifier Personnel ID *</label>
                        <input
                          type="text"
                          required
                          value={verifId}
                          onChange={(e) => setVerifId(e.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label>Verifier Name *</label>
                        <input
                          type="text"
                          required
                          value={verifName}
                          onChange={(e) => setVerifName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Verification Outcome *</label>
                      <select
                        value={verifResult}
                        onChange={(e) => setVerifResult(e.target.value as any)}
                      >
                        <option value="EFFECTIVE">EFFECTIVE - Actions verified as fully implemented</option>
                        <option value="PARTIALLY_EFFECTIVE">PARTIALLY EFFECTIVE</option>
                        <option value="INEFFECTIVE">INEFFECTIVE</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Verification Audit Evidence Notes *</label>
                      <textarea
                        required
                        rows={2}
                        value={verifNotes}
                        onChange={(e) => setVerifNotes(e.target.value)}
                        placeholder="On-site check performed, software revision v2.4 and dispatch training records validated."
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Approve Verification
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.verifications && capa.verifications.length > 0 ? (
                    capa.verifications.map((v) => (
                      <div key={v.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            v.result === 'EFFECTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                          }`}>
                            {v.result}
                          </span>
                          <span className="font-semibold text-gray-800">{v.verifierName} ({v.verifierId})</span>
                        </div>
                        <p className="mt-1 text-gray-700">{v.notes}</p>
                        <div className="text-[11px] text-gray-400 mt-1 font-mono">
                          {v.verifiedAt.substring(0, 10)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No verification records logged yet.</div>
                  )}
                </div>
              </div>

              {/* Long-term Effectiveness Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Long-term Effectiveness Evaluation</h3>
                    <p className="text-xs text-gray-500">Metric testing over defined time horizon to confirm non-recurrence.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowAddEffectiveness(!showAddEffectiveness)}
                      className="btn-secondary"
                    >
                      {showAddEffectiveness ? 'Cancel' : '+ Add Effectiveness Test'}
                    </button>
                  )}
                </div>

                {showAddEffectiveness && (
                  <form onSubmit={handleAddEffectiveness} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Metric Name *</label>
                        <input
                          type="text"
                          required
                          value={effMetric}
                          onChange={(e) => setEffMetric(e.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label>Acceptance Criteria *</label>
                        <input
                          type="text"
                          required
                          value={effCriteria}
                          onChange={(e) => setEffCriteria(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-grid-3">
                      <div className="form-field">
                        <label>Baseline Value</label>
                        <input
                          type="text"
                          value={effBaseline}
                          onChange={(e) => setEffBaseline(e.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label>Target Value</label>
                        <input
                          type="text"
                          value={effTarget}
                          onChange={(e) => setEffTarget(e.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label>Actual Value</label>
                        <input
                          type="text"
                          value={effActual}
                          onChange={(e) => setEffActual(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Effectiveness Result</label>
                        <select
                          value={effResult}
                          onChange={(e) => setEffResult(e.target.value as any)}
                        >
                          <option value="EFFECTIVE">EFFECTIVE - Non-recurrence proven</option>
                          <option value="PARTIALLY_EFFECTIVE">PARTIALLY EFFECTIVE</option>
                          <option value="INEFFECTIVE">INEFFECTIVE</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Evaluation Date</label>
                        <input
                          type="date"
                          value={effDate}
                          onChange={(e) => setEffDate(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Evaluation Notes</label>
                      <textarea
                        rows={2}
                        value={effNotes}
                        onChange={(e) => setEffNotes(e.target.value)}
                        placeholder="Target criteria sustained throughout 90-day post-implementation monitoring period."
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Save Effectiveness Evaluation
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.effectivenessChecks && capa.effectivenessChecks.length > 0 ? (
                    capa.effectivenessChecks.map((ec) => (
                      <div key={ec.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              ec.result === 'EFFECTIVE' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                            }`}>
                              {ec.result}
                            </span>
                            <span className="font-semibold text-gray-800">{ec.metricName}</span>
                          </div>
                          <div className="text-[11px] text-gray-400 font-mono">
                            {ec.evaluationDate.substring(0, 10)}
                          </div>
                        </div>
                        <div className="mt-1 text-gray-600">
                          <span className="font-medium">Criteria:</span> {ec.criteria} | <span className="font-medium">Target:</span> {ec.targetValue} | <span className="font-medium">Actual:</span> {ec.actualValue}
                        </div>
                        {ec.notes && <p className="mt-1 text-gray-700 italic">{ec.notes}</p>}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No effectiveness evaluations logged yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: EXTENSIONS & CLOSURE */}
          {activeTab === 'EXTENSIONS_CLOSURE' && (
            <div className="space-y-6">
              {/* Extensions Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">Deadline Extension Requests</h3>
                    <p className="text-xs text-gray-500">Formal extension governance with operational justification and risk evaluation.</p>
                  </div>
                  {!isClosed && (
                    <button
                      type="button"
                      onClick={() => setShowExtensionModal(!showExtensionModal)}
                      className="btn-secondary"
                    >
                      {showExtensionModal ? 'Cancel' : '+ Request Extension'}
                    </button>
                  )}
                </div>

                {showExtensionModal && (
                  <form onSubmit={handleRequestExtension} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>New Target Due Date *</label>
                        <input
                          type="date"
                          required
                          value={extNewDate}
                          onChange={(e) => setExtNewDate(e.target.value)}
                        />
                      </div>
                      <div className="form-field">
                        <label>Operational Justification *</label>
                        <input
                          type="text"
                          required
                          value={extReason}
                          onChange={(e) => setExtReason(e.target.value)}
                          placeholder="Vendor delivery timeline deferral, simulator schedule shift..."
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Risk Assessment During Deferral</label>
                      <input
                        type="text"
                        value={extRisk}
                        onChange={(e) => setExtRisk(e.target.value)}
                        placeholder="Interim controls remain in force; no secondary operational risk identified."
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Submit Extension Request
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-2">
                  {capa.extensions && capa.extensions.length > 0 ? (
                    capa.extensions.map((ext) => (
                      <div key={ext.id} className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs flex justify-between items-center">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              ext.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : ext.status === 'REJECTED' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {ext.status}
                            </span>
                            <span className="font-semibold text-gray-800 font-mono">
                              {ext.oldDueDate.substring(0, 10)} &rarr; {ext.newDueDate.substring(0, 10)}
                            </span>
                          </div>
                          <p className="mt-1 text-gray-700">{ext.reason}</p>
                        </div>
                        {ext.status === 'REQUESTED' && !isClosed && (
                          <button
                            type="button"
                            onClick={() => handleApproveExtension(ext.id)}
                            className="px-3 py-1 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700"
                          >
                            Approve Extension
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-400 italic">No deadline extension requests on file.</div>
                  )}
                </div>
              </div>

              {/* Close / Reopen CAPA Section */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
                <h3 className="font-bold text-gray-800 text-sm mb-1">CAPA Formal Closure & Sealing</h3>
                <p className="text-xs text-gray-500 mb-2">
                  Closure prerequisites: Root cause analysis accepted, independent verification completed, and effectiveness verified.
                </p>

                {!isClosed ? (
                  <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="form-field">
                      <label>Closure Statement / Manager Signoff Note *</label>
                      <textarea
                        rows={2}
                        value={closeComment}
                        onChange={(e) => setCloseComment(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={handleCloseCapa}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700 shadow-sm transition"
                      >
                        Formally Close CAPA Case
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 bg-emerald-50/50 p-4 rounded-xl border border-emerald-200">
                    <div className="flex items-center space-x-2 text-emerald-800 font-bold text-sm">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>This CAPA case has been formally closed and sealed (Immutable).</span>
                    </div>
                    <p className="text-xs text-emerald-700 italic">
                      "{capa.closureComment || 'Closure signoff confirmed.'}" — {capa.closedBy || 'Director'} ({capa.closedAt?.substring(0, 10)})
                    </p>

                    {!showReopenModal ? (
                      <button
                        type="button"
                        onClick={() => setShowReopenModal(true)}
                        className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700 mt-2 transition"
                      >
                        Reopen CAPA
                      </button>
                    ) : (
                      <form onSubmit={handleReopenCapa} className="pt-3 border-t border-emerald-200 space-y-3">
                        <div className="form-field">
                          <label>Justification for Reopening *</label>
                          <input
                            type="text"
                            required
                            value={reopenReason}
                            onChange={(e) => setReopenReason(e.target.value)}
                            placeholder="e.g. Recurrence of defect detected during follow-up spot surveillance."
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowReopenModal(false)}
                            className="btn-secondary"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition"
                          >
                            Confirm Reopening
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer justify-between">
          <span className="text-gray-400 font-mono text-[11px]">
            Created: {capa.createdAt?.substring(0, 10)} | Org: {capa.organizationId}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
